import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { preOsSchema, type IncidentProps } from "@interfaces";
import {
  popupError,
  getCurrentTicketId,
  popupSuccess,
  showMacroContent,
  getCurrentRequesterEmail,
  getCurrentRequesterName,
  getTicketFieldValue,
  getProdutoFromTicket,
  getCurrentRequesterCpf,
} from "@service/zendesk";
import { resolveFields } from "@service/zendesk/fieldResolver";
import { CreateIncident } from "@service/Incidents/Incident";
import { returnCurrentUserData } from "@service/user";
import { AxiosError } from "axios";

export const usePreOS = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cepLoading, setCepLoading] = useState<boolean>(false);

  const { control, handleSubmit, watch, reset, setValue } = useForm({
    resolver: zodResolver(preOsSchema),
    defaultValues: {
      osPrincipal: "",
      assunto: "",
      descricao: "",
      codigoPosto: "",
      numeroSerie: "",
      statusSolicitacao: "aguardando_os",
      nomeConsumidor: "",
      emailConsumidor: "",
      cep: "",
      endereco_2: "",
      numero: "",
      bairro: "",
      cidade: "",
      estado: "",
      telefoneConsumidor: "",
      notaFiscal: "",
      anexos: [],
      dataCompra: "",
      defeitoReclamado: "",
      aparenciaProduto: "",
      referenciaProduto: "",
      descricaoProduto: "",
      cpf: "",
    },
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const currentUserData = await returnCurrentUserData();

        const f = await resolveFields();

        const objectDescription = await getProdutoFromTicket(f("Produto (objeto)"));
        const ticketId = await getCurrentTicketId();
        const requesterName = await getCurrentRequesterName();
        const requesterEmail = await getCurrentRequesterEmail();
        const cpf = await getCurrentRequesterCpf();
        const CEP = currentUserData?.user_fields.cep__endereco;
        const endereco = currentUserData?.user_fields.endereco_2;
        const numero = currentUserData?.user_fields.numero;
        const serie = await getTicketFieldValue(f("[SAC AT] Serial"));
        const bairro = currentUserData?.user_fields.bairro;
        const estado = currentUserData?.user_fields.estado;
        const cidade = currentUserData?.user_fields.cidade;
        const telefoneConsumidor = currentUserData?.user_fields.outro_telefone;
        const notaFiscal = await getTicketFieldValue(f("[Geral] NF da Revenda"));

        const dateString = await getTicketFieldValue(f("[Geral] Data da compra"));
        const parsedDate = dateString ? new Date(dateString) : null;
        const dataCompra = parsedDate ? parsedDate.toISOString().split("T")[0] : "";

        const defeitoReclamado = await getTicketFieldValue(f("Defeito reclamado"));
        const aparenciaProduto = await getTicketFieldValue(f("[Ordem de Servico] Aparência"));

        const descricaoProduto = objectDescription?.custom_object_fields.descricao_do_produto;
        const codigoProduto = objectDescription?.custom_object_fields.codigo_do_produto;

        const codigoPosto = await getTicketFieldValue(f("[Ordem de Servico] Código do posto"));

        // pegamos a macro aqui
        const response = await showMacroContent();
        const macroContent = response.actions[0].value;

        const parseMacroToPlainText = (html: string): string => {
          if (!html) return "";

          let text = html;

          // precisamos converter o br para \n tendo em vista que o campo nao aceita itens html
          text = text.replace(/<br\s*\/?>/gi, "\n");

          // <p> = \n\n
          text = text.replace(/<\/p>/gi, "\n\n");
          text = text.replace(/<p[^>]*>/gi, "");

          // limpamos o restante de html
          text = text.replace(/<[^>]+>/g, "");

          // fazemos uma conversão de espaços não quebraveis
          text = text.replace(/&nbsp;/g, " ");

          return text.trimEnd(); // trim só no final para garantir espaçamento ;)
        };

        const plainText = parseMacroToPlainText(macroContent ?? "");

        reset({
          assunto: `Pre-OS - ${ticketId?.toString()}`,
          osPrincipal: ticketId?.toString() ?? "",
          descricao: plainText,

          nomeConsumidor: requesterName ?? "",
          emailConsumidor: requesterEmail ?? "",
          cep: CEP ?? "",
          endereco_2: endereco ?? "",
          numero: numero?.toString() ?? "",
          numeroSerie: serie ?? "",
          bairro: bairro ?? "",
          estado: estado ?? "",
          telefoneConsumidor: telefoneConsumidor ?? "",
          notaFiscal: notaFiscal ?? "",
          cidade: cidade ?? "",
          dataCompra: dataCompra ?? "",
          defeitoReclamado: defeitoReclamado ?? "",
          aparenciaProduto: aparenciaProduto ?? "",
          descricaoProduto: descricaoProduto ?? "",
          referenciaProduto: codigoProduto ?? "",
          codigoPosto: codigoPosto ?? "",
          cpf: cpf ?? "",
        });
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        popupError("Erro ao carregar dados iniciais. Tente novamente.", false);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Nota: 'reset' foi intencionalmente omitido das dependências para evitar loop infinito

  // fazendo substituição das chaves por valores:
  const fillTemplate = (template: string, data: Record<string, any>): string => {
    return template.replace(/{{\s*([\w]+)\s*}}/g, (_, key) => {
      const value = data[key];
      return value != null && value !== "" ? value : "";
    });
  };

  const onSubmit = async (data: IncidentProps) => {
    setLoading(true);

    try {
      const comentarioInterno = fillTemplate(data.descricao, data);

      const response = await CreateIncident({
        ...data,
        descricao: comentarioInterno,
      });

      if (response?.ticket.id) {
        popupSuccess(`Incidente criado com sucesso ${response.ticket.id}`, false);
        navigate(`/success/Incidente/${response.ticket.id}`);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errTicket = err.response?.data?.details?.ticket_form_id?.[0]?.description;
        if (errTicket) {
          popupError(`Erro ao criar incidente: ${errTicket}`, false);
          return;
        }
        popupError("Erro ao criar incidente. Tente novamente mais tarde.", false);
      } else {
        console.error("Erro desconhecido ao criar incidente:", err);
        popupError("Erro ao criar incidente. Tente novamente mais tarde.", false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.filter(
      (file) => !files.some((f) => f.name === file.name && f.lastModified === file.lastModified)
    );
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (fileName: string) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const onError = (errors: FieldErrors<IncidentProps>) => {
    const messages = Object.values(errors)
      .map((err) => err?.message)
      .filter(Boolean) as string[];
    popupError(messages.join("\n"), false);
  };

  const backToHome = () => {
    navigate("/");
  };

  return {
    backToHome,
    files,
    handleFileChange,
    handleRemoveFile,
    control,
    handleSubmit,
    watch,
    setValue,
    onSubmit,
    onError,
    loading,
    cepLoading,
    setCepLoading,
  };
};
